import {
  Box,
  Circle,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { TaskList } from "@src/components/modules/notifications/listing/components/TaskList";
import { Icon } from "@src/components/ui-kit/Icon";
import { LoadingOverlay } from "@src/components/ui-kit/LoadingOverlay/LoadingOverlay";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { HeaderContainer, LinkToTable } from "../listing/components";
import { TaskNotificationsSettings } from "../listing/TaskNotificationsSettings";

function useFetchNotifications() {
  const { notificationsStore: store } = useStore();

  useEffect(() => {
    if (!store.tasksPopoverDisclosure.isOpen) return;
    store.setTaskNotifications({
      taskAndCommunicationNotifications: {
        data: [],
        paginatorInfo: {
          count: 0,
          total: 0,
          perPage: 5,
          lastPage: 1,
          currentPage: 1,
          hasMorePages: false,
        },
      },
    });
    store.refetchTaskNotifications();
  }, [store.showReadTaskNotifications, store.tasksPopoverDisclosure.isOpen]);
}

const TaskNotificationsPopupList = () => {
  const { notificationsStore: store } = useStore();

  useFetchNotifications();

  return (
    <Popover
      isOpen={store.tasksPopoverDisclosure.isOpen}
      onClose={store.tasksPopoverDisclosure.onClose}
      onOpen={() => {
        store.tasksPopoverDisclosure.onOpen();
        store.refetchTaskNotifications?.();
        store.fetchNotificationsUnreadCount();
      }}
      placement="bottom-end"
    >
      <PopoverTrigger>
        <Box pos="relative">
          <IconButton
            aria-label={t`show notifications`}
            icon={<Icon name="message-chat-square" w="6" h="6" />}
            variant="ghost"
          />
          {store.unreadTaskNotificationsCount !== 0 && (
            <Circle
              pos="absolute"
              top="0"
              right="0"
              mt="-1"
              bg="red.500"
              size="5"
            >
              <Text color="white" fontSize="2xs" fontWeight="bold">
                {store.unreadTaskNotificationsCount}
              </Text>
            </Circle>
          )}
        </Box>
      </PopoverTrigger>

      <Portal>
        <PopoverContent
          overflowX={{ base: "scroll", md: "unset" }}
          overflowY="auto"
          minW={{
            base: "100vw",
            md: "550px",
          }}
          maxH="calc(100dvh - 55px)"
        >
          <PopoverHeader p="4">
            <HeaderContainer forModule="popup">
              <Box>
                <h2 className="text-xl">
                  <Trans>Notifications</Trans>
                </h2>
              </Box>
              <TaskNotificationsSettings
                isLoading={store.taskNotificationsLoading}
                notificationCount={store.taskNotifications.length}
                showRead={store.showReadTaskNotifications}
                onMark={() => store.refetch?.()}
                onShowReadChanged={store.setShowReadTaskNotifications}
              />
            </HeaderContainer>
          </PopoverHeader>

          <PopoverBody
            pos="relative"
            overflowX={{ base: "scroll", md: "unset" }}
            p="0"
          >
            <LoadingOverlay
              isLoading={store.taskNotificationsLoading}
              size="sm"
              position="absolute"
            >
              <Box minW={{ base: "600px", md: "full" }} h="full">
                <TaskList
                  height="50rem"
                  isLoading={store.taskNotificationsLoading}
                  taskNotifications={store.taskNotifications}
                  containerClassName="border-none py-6"
                />
              </Box>
            </LoadingOverlay>
          </PopoverBody>

          <PopoverFooter p="4" textAlign="center">
            <LinkToTable
              onClick={() => store.tasksPopoverDisclosure.onClose()}
            />
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default observer(TaskNotificationsPopupList);
